import { revalidateLogic, useForm } from '@tanstack/react-form';
import { forgotCodeSchema, forgotEmailSchema } from '../schemas/auth';
import { ValidateFormRefObject } from './useAuthForm';
import { useTanstackFormErrors } from '#app/hooks/useTanstackFormErrors';

/** Серверные ошибки (onServer) снимаются в handleSubmit через clearServerErrors — как useAccountTanstackForm. */

interface UseForgotEmailFormParams {
  initialEmail?: string;
  onSubmit: (values: Record<string, any>, obj: ValidateFormRefObject) => void;
}

/** Шаг 1: только email → request-forgot */
export function useForgotEmailForm({ initialEmail = '', onSubmit }: UseForgotEmailFormParams) {
  const tanstackForm = useForm({
    defaultValues: {
      email: initialEmail,
    },
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onChange: forgotEmailSchema as any,
      onSubmit: forgotEmailSchema as any,
    },
    canSubmitWhenInvalid: true,
    onSubmit: handleFormSubmit,
  });

  const { setServerErrors, clearServerErrors, hasClientErrors, hasServerErrors } =
    useTanstackFormErrors(tanstackForm);

  function handleFormSubmit({ value }: { value: Record<string, string> }) {
    if (hasClientErrors && !hasServerErrors) return;
    onSubmit(value, validateFormObj);
  }

  const validateFormObj: ValidateFormRefObject = {
    state: { formErrors: {} },
    setState: (updater) => {
      if (typeof updater === 'function') {
        const next = updater({ formErrors: {} });
        setServerErrors(next?.formErrors ?? {});
      }
    },
  };

  const handleSubmit = () => {
    clearServerErrors();
    tanstackForm.handleSubmit();
  };

  return {
    tanstackForm,
    handleSubmit,
  };
}

interface UseForgotCodeFormParams {
  onSubmit: (values: Record<string, any>, obj: ValidateFormRefObject) => void;
}

/** Шаг 2: код + новый пароль → forgot (отдельная форма, свой submissionAttempts) */
export function useForgotCodeForm({ onSubmit }: UseForgotCodeFormParams) {
  const tanstackForm = useForm({
    defaultValues: {
      code: '',
      password: '',
      password_confirmation: '',
      form: 'client',
    },
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onChange: forgotCodeSchema as any,
      onSubmit: forgotCodeSchema as any,
    },
    canSubmitWhenInvalid: true,
    onSubmit: handleFormSubmit,
  });

  const { setServerErrors, clearServerErrors, hasClientErrors, hasServerErrors } =
    useTanstackFormErrors(tanstackForm);

  function handleFormSubmit({ value }: { value: Record<string, string> }) {
    if (hasClientErrors && !hasServerErrors) return;
    onSubmit(value, validateFormObj);
  }

  const validateFormObj: ValidateFormRefObject = {
    state: { formErrors: {} },
    setState: (updater) => {
      if (typeof updater === 'function') {
        const next = updater({ formErrors: {} });
        setServerErrors(next?.formErrors ?? {});
      }
    },
  };

  const handleSubmit = () => {
    clearServerErrors();
    tanstackForm.handleSubmit();
  };

  return {
    tanstackForm,
    handleSubmit,
  };
}
